import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Alert, ConfirmModal, EmptyState, LoadingScreen, spacing } from "@xplatform/design-system";
import { useReviewApi } from "@hooks/useReviewApi";
import { usePermissions } from "@hooks/usePermissions";
import type {
  ReviewCounts,
  ReviewDecisionRequest,
  ReviewImageDetail,
  ReviewImageListResponse,
  ReviewImageSummary,
  ReviewRunSummary,
  RunStatus,
} from "@models/review";
import { errorMessageKey } from "@utils/errorMessages";
import { ReviewToolbar } from "./ReviewToolbar";
import { getPublishBlocker } from "./publishBlocker";
import { ReviewSidebar, type ReviewSubStep } from "./ReviewSidebar";
import { FailedSection } from "./FailedSection";
import { imageKey } from "./imageKey";
import { ReviewMain } from "./ReviewMain";
import { FailedImageView } from "./FailedImageView";
import { AllDoneState } from "./AllDoneState";
import type { SaveState } from "./SaveStatus";
import "./ReviewWorkspace.scss";

const ZERO_COUNTS: ReviewCounts = {
  pending: 0,
  approved: 0,
  rejected: 0,
  notApplicable: 0,
  failed: 0,
  regenerable: 0,
};

/** How often to poll the image list while the run is moving (see `pollMode`). */
const POLL_INTERVAL_MS = 5_000;
/** Stop polling after this long (back off): the admin can refresh manually. */
const POLL_MAX_MS = 120_000;

function isPending(image: ReviewImageSummary): boolean {
  return image.reviewState === "pending";
}

function isFailed(image: ReviewImageSummary): boolean {
  return image.status === "FAILED";
}

/**
 * The single review workspace that replaces the old queue / run / image pages.
 * It is a full-height flex column — a slim toolbar over a body of SIDEBAR +
 * MAIN — that sits below the shell's global header. All three routes render it:
 *
 *   review                                  auto-selects the first run awaiting
 *                                           review and its first pending image
 *   review/:runId                           a specific run
 *   review/:runId/:requestId/:objId         a specific image
 *
 * Selecting a run or image updates the URL (keeping deep links), so the routes
 * stay behavioural while the layout is unified. Every review rule is preserved:
 * the decision rules live in `ReviewMain`, Publish lock in the toolbar / done
 * state, and the API's 403 / 409 messages stay authoritative.
 */
export function ReviewWorkspace() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { runId: routeRunId, requestId: routeRequestId, objId: routeObjId } = useParams();

  // The module-rooted path of the `review` base route, derived from the live
  // URL so it is correct at every mount point (shell `/generation/review`,
  // preview `/review`). Every navigate below builds an ABSOLUTE path from this
  // base — a relative `navigate()` resolves against whichever of the three
  // routes matched (`review`, `review/:runId`, `review/:runId/:req/:obj`) and
  // so doubled the runId when fired from the `review/:runId` match (I-46).
  const reviewBasePath = useMemo(() => {
    const marker = "/review";
    const idx = pathname.indexOf(marker);
    if (idx === -1) return pathname.replace(/\/$/, "");
    return pathname.slice(0, idx + marker.length);
  }, [pathname]);
  const runPath = useCallback(
    (runId: string) => `${reviewBasePath}/${runId}`,
    [reviewBasePath],
  );
  const imagePath = useCallback(
    (runId: string, requestId: string, objId: string) =>
      `${reviewBasePath}/${runId}/${requestId}/${objId}`,
    [reviewBasePath],
  );
  const api = useReviewApi();
  const { canAdmin } = usePermissions();
  // `generation.admin` drives both Publish and Regenerate (D21: admin only).
  const canPublish = canAdmin;
  const canRegenerate = canAdmin;

  const [runs, setRuns] = useState<ReviewRunSummary[] | null>(null);
  const [runsErrorKey, setRunsErrorKey] = useState<string | null>(null);

  const [imageList, setImageList] = useState<ReviewImageListResponse | null>(null);
  const [listErrorKey, setListErrorKey] = useState<string | null>(null);

  const [detail, setDetail] = useState<ReviewImageDetail | null>(null);
  const [detailErrorKey, setDetailErrorKey] = useState<string | null>(null);

  const [collapsed, setCollapsed] = useState(false);
  const [activeSub, setActiveSub] = useState<ReviewSubStep>("q1");
  const [q1Answered, setQ1Answered] = useState(false);
  const [saveState, setSaveState] = useState<SaveState>("idle");

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [publishing, setPublishing] = useState(false);

  // ── Regenerate (F9/D17) ───────────────────────────────────────────────────
  // The failed image the admin is viewing (requestId/objId), or null.
  const [failedKey, setFailedKey] = useState<string | null>(null);
  // The ticked regenerable images, keyed requestId/objId.
  const [selectedFailed, setSelectedFailed] = useState<Set<string>>(new Set());
  const [regenConfirmOpen, setRegenConfirmOpen] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  // The number of images in the in-flight / just-sent regenerate, for the
  // banner; null when no regenerate is active.
  const [regenBannerCount, setRegenBannerCount] = useState<number | null>(null);
  const [regenErrorKey, setRegenErrorKey] = useState<string | null>(null);

  // ── Load the runs in GENERATED, i.e. awaiting review (once). ─────────────
  useEffect(() => {
    let live = true;
    void (async () => {
      try {
        const page = await api.listGeneratedRuns();
        if (live) setRuns(page.items);
      } catch (error) {
        if (live) {
          setRuns([]);
          setRunsErrorKey(errorMessageKey(error));
        }
      }
    })();
    return () => {
      live = false;
    };
  }, [api]);

  // The active run: the route's runId if present, else the first GENERATED run.
  const activeRunId = useMemo(() => {
    if (routeRunId) return routeRunId;
    return runs && runs.length > 0 ? runs[0].runId : null;
  }, [routeRunId, runs]);

  // Deep-link: once runs load with none selected, pin the first into the URL.
  useEffect(() => {
    if (!routeRunId && runs && runs.length > 0) {
      navigate(runPath(runs[0].runId), { replace: true });
    }
  }, [routeRunId, runs, navigate, runPath]);

  // ── Load the active run's images. ─────────────────────────────────────────
  useEffect(() => {
    if (!activeRunId) return;
    let live = true;
    setImageList(null);
    setListErrorKey(null);
    setFailedKey(null);
    setSelectedFailed(new Set());
    setRegenBannerCount(null);
    setRegenErrorKey(null);
    void (async () => {
      try {
        const res = await api.listImages(activeRunId);
        if (live) setImageList(res);
      } catch (error) {
        if (live) setListErrorKey(errorMessageKey(error));
      }
    })();
    return () => {
      live = false;
    };
  }, [api, activeRunId]);

  const images = useMemo(() => imageList?.images ?? [], [imageList]);
  const counts = imageList?.counts ?? ZERO_COUNTS;
  const anyPending = counts.pending > 0;
  const runStatus = imageList?.runStatus ?? null;
  // D20: Regenerate and Publish are offered only on a GENERATED run.
  const runParked = runStatus === "GENERATED";
  // Publish is in flight (the return machine is running) until the run settles.
  const returning = runStatus === "PUBLISHING";
  const publishBlocker = getPublishBlocker(runStatus, anyPending);

  // The images that ended FAILED (any reason), shown in the sidebar section.
  const failed = useMemo(() => images.filter(isFailed), [images]);

  // The active failed image (read-only view), resolved from `failedKey`.
  const activeFailed = useMemo(
    () => failed.find((img) => imageKey(img) === failedKey),
    [failed, failedKey],
  );

  const firstPendingIndex = useMemo(
    () => images.findIndex(isPending),
    [images],
  );

  // The active image index, resolved from the route or the first pending one.
  const activeIndex = useMemo(() => {
    if (routeRequestId && routeObjId) {
      const idx = images.findIndex(
        (img) => img.requestId === routeRequestId && img.objId === routeObjId,
      );
      if (idx !== -1) return idx;
    }
    return firstPendingIndex;
  }, [routeRequestId, routeObjId, images, firstPendingIndex]);

  const activeImage = activeIndex >= 0 ? images[activeIndex] : undefined;

  // Deep-link: when a run is active with no image in the URL and one is
  // pending, pin the first pending image into the URL.
  useEffect(() => {
    if (
      activeRunId &&
      !routeObjId &&
      imageList &&
      firstPendingIndex !== -1
    ) {
      const img = images[firstPendingIndex];
      navigate(imagePath(activeRunId, img.requestId, img.objId), { replace: true });
    }
  }, [activeRunId, routeObjId, imageList, firstPendingIndex, images, navigate, imagePath]);

  // ── Load the active image detail (pending image under review). ────────────
  useEffect(() => {
    // A selected failed image takes over the main area; its detail loads below.
    if (failedKey || !activeRunId || !activeImage) {
      if (!failedKey) setDetail(null);
      return;
    }
    let live = true;
    setDetail(null);
    setDetailErrorKey(null);
    setActiveSub("q1");
    setQ1Answered(false);
    setSaveState("idle");
    void (async () => {
      try {
        const d = await api.getImage(activeRunId, activeImage.requestId, activeImage.objId);
        if (live) setDetail(d);
      } catch (error) {
        if (live) setDetailErrorKey(errorMessageKey(error));
      }
    })();
    return () => {
      live = false;
    };
    // Depend on the identity FIELDS, not the `activeImage` object, so a
    // list refresh that returns an equal image does not refetch the detail.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, activeRunId, activeImage?.requestId, activeImage?.objId, failedKey]);

  // ── Load the active FAILED image detail (read-only view, F9/D17). ─────────
  useEffect(() => {
    if (!activeRunId || !activeFailed) return;
    let live = true;
    setDetail(null);
    setDetailErrorKey(null);
    void (async () => {
      try {
        const d = await api.getImage(activeRunId, activeFailed.requestId, activeFailed.objId);
        if (live) setDetail(d);
      } catch (error) {
        if (live) setDetailErrorKey(errorMessageKey(error));
      }
    })();
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, activeRunId, activeFailed?.requestId, activeFailed?.objId]);

  const pendingByRun = useMemo(() => {
    const map: Record<string, number> = {};
    // Only the active run's exact pending count is known; others show their
    // image count as an upper bound until opened.
    for (const run of runs ?? []) map[run.runId] = run.imageCount;
    if (activeRunId) map[activeRunId] = counts.pending;
    return map;
  }, [runs, activeRunId, counts.pending]);

  // ── Navigation ────────────────────────────────────────────────────────────
  const selectRun = useCallback(
    (runId: string) => navigate(runPath(runId)),
    [navigate, runPath],
  );

  const selectImage = useCallback(
    (index: number) => {
      const img = images[index];
      if (img && activeRunId) navigate(imagePath(activeRunId, img.requestId, img.objId));
    },
    [images, activeRunId, navigate, imagePath],
  );

  const goNextPending = useCallback(() => {
    const idx = images.findIndex(isPending);
    if (idx !== -1 && activeRunId) {
      const img = images[idx];
      navigate(imagePath(activeRunId, img.requestId, img.objId));
    }
  }, [images, activeRunId, navigate, imagePath]);

  // Replace the active image in the local list after a decision, so the
  // sidebar icons and counts update without a full reload.
  const applyDecision = useCallback(
    (state: "approved" | "rejected") => {
      setImageList((prev) => {
        if (!prev || !activeImage) return prev;
        const nextImages = prev.images.map((img) =>
          img.requestId === activeImage.requestId && img.objId === activeImage.objId
            ? { ...img, reviewState: state }
            : img,
        );
        const nextCounts: ReviewCounts = {
          ...prev.counts,
          pending: Math.max(0, prev.counts.pending - 1),
          approved: prev.counts.approved + (state === "approved" ? 1 : 0),
          rejected: prev.counts.rejected + (state === "rejected" ? 1 : 0),
        };
        return { ...prev, counts: nextCounts, images: nextImages };
      });
    },
    [activeImage],
  );

  const decide = useCallback(
    async (body: ReviewDecisionRequest) => {
      if (!activeRunId || !activeImage) throw new Error("no active image");
      return api.decide(activeRunId, activeImage.requestId, activeImage.objId, body);
    },
    [api, activeRunId, activeImage],
  );

  const doPublish = useCallback(async () => {
    if (!activeRunId) return;
    setPublishing(true);
    setListErrorKey(null);
    try {
      await api.publish(activeRunId);
      setConfirmOpen(false);
      // The starter moves the run to PUBLISHING; show it and poll until settled.
      setImageList((prev) => (prev ? { ...prev, runStatus: "PUBLISHING" } : prev));
    } catch (error) {
      setConfirmOpen(false);
      setListErrorKey(errorMessageKey(error));
      // The run may have changed under us; refresh the images/counts.
      try {
        setImageList(await api.listImages(activeRunId));
      } catch {
        /* keep the publish error visible */
      }
    } finally {
      setPublishing(false);
    }
  }, [api, activeRunId]);

  // ── Regenerate: selection, open a failed image, send, poll (F9/D17). ──────
  const selectFailed = useCallback((image: ReviewImageSummary) => {
    setFailedKey(imageKey(image));
  }, []);

  const toggleFailed = useCallback((key: string) => {
    setSelectedFailed((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const toggleAllFailed = useCallback(() => {
    setSelectedFailed((prev) => {
      const regenerable = failed.filter((i) => i.regenerable);
      const allKeys = regenerable.map(imageKey);
      const allSelected = allKeys.length > 0 && allKeys.every((k) => prev.has(k));
      return allSelected ? new Set() : new Set(allKeys);
    });
  }, [failed]);

  const doRegenerate = useCallback(async () => {
    if (!activeRunId) return;
    const refs = failed
      .filter((i) => i.regenerable && selectedFailed.has(imageKey(i)))
      .map((i) => ({ requestId: i.requestId, objId: i.objId }));
    if (refs.length === 0) return;

    setRegenerating(true);
    setRegenErrorKey(null);
    try {
      const res = await api.regenerate(activeRunId, { images: refs });
      setRegenConfirmOpen(false);
      setSelectedFailed(new Set());
      setFailedKey(null);
      // The starter moves the run to PROCESSING; show the banner and poll.
      setRegenBannerCount(res.count);
      // Reflect the imminent PROCESSING state locally so the run is not
      // dropped from the toolbar while we poll.
      setImageList((prev) => (prev ? { ...prev, runStatus: "PROCESSING" } : prev));
    } catch (error) {
      setRegenConfirmOpen(false);
      setRegenErrorKey(errorMessageKey(error));
    } finally {
      setRegenerating(false);
    }
  }, [api, activeRunId, failed, selectedFailed]);

  // Poll the image list while the run is moving, every 5s, with a 2-minute
  // back-off after which the admin can refresh manually:
  //   regenerate  -> until the run is GENERATED again (it parks for review);
  //   publish     -> until the run settles (COMPLETED, or FAILED).
  const pollMode: "regenerate" | "publish" | null =
    regenBannerCount !== null ? "regenerate" : runStatus === "PUBLISHING" ? "publish" : null;

  useEffect(() => {
    if (!pollMode || !activeRunId) return;
    let live = true;
    const startedAt = Date.now();
    let timer: ReturnType<typeof setTimeout> | undefined;

    const settled = (status: RunStatus): boolean =>
      pollMode === "regenerate" ? status === "GENERATED" : status !== "PUBLISHING";

    const poll = async () => {
      try {
        const res = await api.listImages(activeRunId);
        if (!live) return;
        setImageList(res);
        if (settled(res.runStatus)) {
          if (pollMode === "regenerate") setRegenBannerCount(null);
          return;
        }
      } catch {
        /* transient; keep polling until the back-off */
      }
      if (!live) return;
      if (Date.now() - startedAt >= POLL_MAX_MS) {
        // Back off: stop polling; the banner stays so the admin can refresh.
        return;
      }
      timer = setTimeout(() => void poll(), POLL_INTERVAL_MS);
    };

    timer = setTimeout(() => void poll(), POLL_INTERVAL_MS);
    return () => {
      live = false;
      if (timer) clearTimeout(timer);
    };
  }, [api, activeRunId, pollMode]);

  // Avoid flicker: remember whether we ever had runs to show a stable shell.
  const everLoaded = useRef(false);
  if (runs !== null) everLoaded.current = true;

  // ── Render ────────────────────────────────────────────────────────────────
  if (runs === null && !runsErrorKey) {
    return <LoadingScreen message={t("common.loading")} />;
  }

  if (runsErrorKey) {
    return (
      <Alert color="rapidRed" title={t("review.errors.title")} m={spacing.lg}>
        {t(runsErrorKey)}
      </Alert>
    );
  }

  // With no GENERATED run and no deep link, there is nothing to show. A deep
  // link still renders its run: it may be PUBLISHING or PROCESSING, which the
  // list no longer returns (the images route serves it in any status).
  if ((runs ?? []).length === 0 && !routeRunId) {
    return (
      <EmptyState
        title={t("review.queue.empty.title")}
        description={t("review.queue.empty.description")}
        m={spacing.xl}
      />
    );
  }

  const decisionAnswered =
    activeImage?.reviewState === "approved" || activeImage?.reviewState === "rejected";

  // Keep the open run in the toolbar's select even while it is PROCESSING
  // because of a regenerate (it drops out of `GET /review/runs`, which only
  // lists GENERATED). F9 requirement (5).
  const baseRuns = runs ?? [];
  const activeRun = baseRuns.find((r) => r.runId === activeRunId);
  const toolbarRuns: ReviewRunSummary[] =
    activeRunId && !activeRun && runStatus
      ? [
          ...baseRuns,
          {
            runId: activeRunId,
            status: runStatus,
            manualCount: 0,
            imageCount: images.length,
            createdAt: "",
            updatedAt: "",
          },
        ]
      : baseRuns;

  // The main area shows: a selected failed image (read-only), else a pending
  // image under review, else the all-done state. The sidebar + failed section
  // render whenever the run has any pending OR failed image.
  const showReviewBody = anyPending || failed.length > 0;
  const showBanner = regenBannerCount !== null;

  return (
    <div className="review-workspace">
      <ReviewToolbar
        canPublish={canPublish}
        runs={toolbarRuns}
        pendingByRun={pendingByRun}
        activeRunId={activeRunId}
        onSelectRun={selectRun}
        saveState={saveState}
        publishBlocker={publishBlocker}
        onPublish={() => setConfirmOpen(true)}
      />

      {returning && (
        <Alert
          color="blueprint"
          title={t("review.publish.returningTitle")}
          className="review-workspace__banner"
        >
          {t("review.publish.returning")}
        </Alert>
      )}

      {runStatus === "COMPLETED" && (
        <Alert color="blueprint" className="review-workspace__banner">
          {t("review.publish.publishedBanner")}
        </Alert>
      )}

      {runStatus === "FAILED" && (
        <Alert color="rapidRed" title={t("review.errors.title")} className="review-workspace__banner">
          {t("review.publish.failedRun")}
        </Alert>
      )}

      {showBanner && (
        <Alert
          color="blueprint"
          title={t("review.regenerate.bannerTitle")}
          className="review-workspace__banner"
        >
          {t("review.regenerate.banner", { count: regenBannerCount ?? 0 })}
        </Alert>
      )}

      {regenErrorKey && (
        <Alert color="rapidRed" title={t("review.errors.title")} className="review-workspace__banner">
          {t(regenErrorKey)}
        </Alert>
      )}

      <div className="review-workspace__body">
        {imageList === null && !listErrorKey ? (
          <LoadingScreen message={t("common.loading")} />
        ) : listErrorKey ? (
          <Alert color="rapidRed" title={t("review.errors.title")} m={spacing.lg}>
            {t(listErrorKey)}
          </Alert>
        ) : !showReviewBody ? (
          <AllDoneState
            approved={counts.approved}
            rejected={counts.rejected}
            failed={counts.failed}
            canPublish={canPublish}
            publishBlocker={publishBlocker}
            onPublish={() => setConfirmOpen(true)}
          />
        ) : (
          <>
            <div className="review-workspace__nav">
              {anyPending && activeImage && (
                <ReviewSidebar
                  images={images.filter((i) => i.status !== "FAILED")}
                  activeIndex={Math.max(
                    0,
                    images
                      .filter((i) => i.status !== "FAILED")
                      .findIndex(
                        (i) =>
                          i.requestId === activeImage.requestId && i.objId === activeImage.objId,
                      ),
                  )}
                  activeSub={activeSub}
                  q1Answered={q1Answered}
                  decisionAnswered={Boolean(decisionAnswered)}
                  collapsed={collapsed}
                  onToggleCollapsed={() => setCollapsed((c) => !c)}
                  onSelectImage={(idx) => {
                    const nonFailed = images.filter((i) => i.status !== "FAILED");
                    const img = nonFailed[idx];
                    if (img) {
                      setFailedKey(null);
                      selectImage(images.findIndex((i) => i === img));
                    }
                  }}
                  onSelectSub={setActiveSub}
                />
              )}
              <FailedSection
                failed={failed}
                selected={selectedFailed}
                canRegenerate={canRegenerate}
                runParked={runParked}
                submitting={regenerating}
                activeKey={activeFailed ? imageKey(activeFailed) : null}
                onToggle={toggleFailed}
                onToggleAll={toggleAllFailed}
                onSelectImage={selectFailed}
                onRegenerate={() => setRegenConfirmOpen(true)}
              />
            </div>

            {activeFailed ? (
              <FailedImageView
                key={imageKey(activeFailed)}
                image={activeFailed}
                detail={detail}
                loadErrorKey={detailErrorKey}
              />
            ) : anyPending && activeImage ? (
              <ReviewMain
                key={`${activeImage.requestId}/${activeImage.objId}`}
                image={activeImage}
                position={activeIndex + 1}
                detail={detail}
                loadErrorKey={detailErrorKey}
                activeSub={activeSub}
                onSelectSub={setActiveSub}
                onQ1AnsweredChange={setQ1Answered}
                onDecided={applyDecision}
                onSaveStateChange={setSaveState}
                decide={decide}
                onNext={goNextPending}
                hasNext={images.some(isPending)}
              />
            ) : (
              <AllDoneState
                approved={counts.approved}
                rejected={counts.rejected}
                failed={counts.failed}
                canPublish={canPublish}
                publishBlocker={publishBlocker}
                onPublish={() => setConfirmOpen(true)}
              />
            )}
          </>
        )}
      </div>

      <ConfirmModal
        opened={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={() => void doPublish()}
        title={t("review.publish.confirmTitle")}
        message={
          counts.failed > 0
            ? `${t("review.publish.confirmBody")} ${t("review.publish.failedWarning", {
                count: counts.failed,
              })}`
            : t("review.publish.confirmBody")
        }
        confirmLabel={t("review.publish.confirmButton")}
        cancelLabel={t("common.cancel")}
        loading={publishing}
      />

      <ConfirmModal
        opened={regenConfirmOpen}
        onClose={() => setRegenConfirmOpen(false)}
        onConfirm={() => void doRegenerate()}
        title={t("review.regenerate.confirmTitle")}
        message={t("review.regenerate.confirmBody", { count: selectedFailed.size })}
        confirmLabel={t("review.regenerate.confirmButton")}
        cancelLabel={t("common.cancel")}
        loading={regenerating}
      />
    </div>
  );
}
